import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { shiftPickedMentions } from 'flow/utils/ticket-utils';
import { setTicketReplyDraft } from '../InputActions';
export var useTicketMessageContent = function useTicketMessageContent(_ref) {
  var ticketKey = _ref.ticketKey,
    internalDraft = _ref.internalDraft,
    internalDraftMentions = _ref.internalDraftMentions,
    replyExternalDraft = _ref.replyExternalDraft,
    inputFieldRef = _ref.inputFieldRef;
  var dispatch = useAppDispatch();
  var _useState = useState({
      text: internalDraft || '',
      attachment: null,
      imageSrc: null,
      mentions: internalDraftMentions
    }),
    _useState2 = _slicedToArray(_useState, 2),
    messageContentInternal = _useState2[0],
    setMessageContentInternal = _useState2[1];
  var _useState3 = useState({
      text: replyExternalDraft || '',
      attachment: null,
      imageSrc: null
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    messageContentReplyExternal = _useState4[0],
    setMessageContentReplyExternal = _useState4[1];

  // The input component isn't remounted when switching tickets, so reset the
  // editable content to the new ticket's drafts when the ticket changes —
  // otherwise unsent text/attachments carry over (the draft-value sync effects
  // below don't fire when two tickets share the same, e.g. empty, draft). This
  // is React's "reset state when a prop changes" pattern (set during render).
  var _useState5 = useState(ticketKey),
    _useState6 = _slicedToArray(_useState5, 2),
    contentTicketKey = _useState6[0],
    setContentTicketKey = _useState6[1];
  if (ticketKey !== contentTicketKey) {
    setContentTicketKey(ticketKey);
    setMessageContentInternal({
      text: internalDraft || '',
      attachment: null,
      imageSrc: null,
      mentions: internalDraftMentions
    });
    setMessageContentReplyExternal({
      text: replyExternalDraft || '',
      attachment: null,
      imageSrc: null
    });
  }

  // Refs to access content on unmount for draft saving
  var contentInternalRefToAccessOnCleanup = useRef(messageContentInternal);
  var contentExternalReplyRefToAccessOnCleanup = useRef(messageContentReplyExternal);

  // Update message content and caret position based on selected tab
  var updateMessageAndCaretPosition = useCallback(function (text, caretPosition, tabSelected) {
    var setMessageContent = tabSelected === 'internal' ? setMessageContentInternal : setMessageContentReplyExternal;
    setMessageContent(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        text: text,
        mentions: shiftPickedMentions(prev.mentions, prev.text, text, caretPosition)
      });
    });
    if (inputFieldRef.current) {
      inputFieldRef.current.value = text;
      inputFieldRef.current.setSelectionRange(caretPosition, caretPosition);
      inputFieldRef.current.focus();
    }
  }, [inputFieldRef]);

  // Sync internal draft changes from Redux
  useEffect(function () {
    setMessageContentInternal(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        text: internalDraft
      });
    });
  }, [internalDraft]);

  // Sync external draft changes from Redux
  useEffect(function () {
    setMessageContentReplyExternal(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        text: replyExternalDraft
      });
    });
  }, [replyExternalDraft]);

  // Update refs when content changes
  useEffect(function () {
    contentInternalRefToAccessOnCleanup.current = messageContentInternal;
  }, [messageContentInternal]);
  useEffect(function () {
    contentExternalReplyRefToAccessOnCleanup.current = messageContentReplyExternal;
  }, [messageContentReplyExternal]);

  // Save drafts on unmount
  useEffect(function () {
    return function () {
      var _contentInternalRefTo, _contentInternalRefTo2, _contentExternalReply;
      dispatch(setTicketReplyDraft(ticketKey, 'internal', ((_contentInternalRefTo = contentInternalRefToAccessOnCleanup.current) === null || _contentInternalRefTo === void 0 ? void 0 : _contentInternalRefTo.text) || '', (_contentInternalRefTo2 = contentInternalRefToAccessOnCleanup.current) === null || _contentInternalRefTo2 === void 0 ? void 0 : _contentInternalRefTo2.mentions));
      dispatch(setTicketReplyDraft(ticketKey, 'external', ((_contentExternalReply = contentExternalReplyRefToAccessOnCleanup.current) === null || _contentExternalReply === void 0 ? void 0 : _contentExternalReply.text) || ''));
    };
  }, [dispatch, ticketKey]);
  return {
    messageContentInternal: messageContentInternal,
    setMessageContentInternal: setMessageContentInternal,
    messageContentReplyExternal: messageContentReplyExternal,
    setMessageContentReplyExternal: setMessageContentReplyExternal,
    updateMessageAndCaretPosition: updateMessageAndCaretPosition
  };
};